/* Fourth Man Roasters: v2 product + cart actions (ES module).
 *
 * Loaded by snippets/fmr-head.liquid as type="module" so it can import the
 * same CartLinesUpdateEvent class Horizon's own product form uses. It is NOT
 * one of the load-order-sensitive fmr-*.js cart files in layout/theme.liquid
 * and it does not register with window.FMR.onCartRender: everything it adds
 * to the cart drawer is server-rendered Liquid that Horizon's morph keeps in
 * step on its own.
 *
 * What it does (all through document-level delegation, so morphed or freshly
 * inserted markup always works):
 *
 * 1. [data-fmr-add-both]   "Try both origins" / "Add the other origin".
 *    One POST /cart/add.js with every item, then the same event Horizon's
 *    product form dispatches. The drawer auto-opens exactly as it does after
 *    any add (cart-drawer.js listens for action 'add'), the cart count bumps,
 *    and the drawer section morphs from the bundled section HTML.
 *
 * 2. [data-fmr-club-switch] "Switch to the Coffee Club" on a cart line.
 *    POST /cart/change.js { id: line key, quantity, selling_plan } with the
 *    plan id Liquid read from that variant's own allocations, then an
 *    'update' event so the drawer (or cart page) morphs.
 *
 * 3. [data-fmr-both] on the product page follows the Roast picker, so the
 *    roast and price it shows are the ones it will add.
 *
 * 4. [data-fmr-notify] sold-out swap: on every variant change Horizon only
 *    morphs the picker, so this copies data-sold-out from the fresh block in
 *    the 'shopify:product:select' event HTML.
 *
 * Never breaks checkout: no input here has a name, nothing sits inside
 * #cart-form's submit path, and every failure path re-enables the button and
 * leaves the cart as Shopify returned it.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function getCart() {
  const r = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
  return r.json();
}

function openDrawerIfClosed() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) return;
  // cart-drawer.js auto-opens on 'add' when the theme setting allows it. This
  // only covers the case where that setting is off.
  if (!drawer.hasAttribute('open') && typeof drawer.open === 'function') drawer.open();
}

function setStatus(btn, text) {
  const wrap = btn.closest('[data-fmr-both]');
  const status = wrap && wrap.querySelector('[data-fmr-both-status]');
  if (status) status.textContent = text || '';
}

/* ---------- 1. add one or more variants ---------- */
async function addItems(items, source) {
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  const itemCount = items.reduce((n, it) => n + it.quantity, 0);

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: source.closest('cart-items-component, .cart-drawer__items') ? 'cart' : 'product',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
      promise: deferred.promise,
    })
  );

  let response;
  try {
    const r = await fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: items.map((it) => ({ id: Number(it.id), quantity: it.quantity })),
        sections: ids.join(','),
        sections_url: window.location.pathname,
      }),
    });
    response = await r.json();
  } catch (err) {
    deferred.reject(err);
    source.dispatchEvent(new CartErrorEvent({ error: (err && err.message) || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    throw err;
  }

  const cart = await getCart().catch(() => null);
  const failed = Boolean(response && response.status);
  if (failed) {
    source.dispatchEvent(
      new CartErrorEvent({
        error: response.message || 'Add to cart failed',
        code: 'INVALID',
        detail: { description: response.description, errors: response.errors },
      })
    );
  }
  if (cart) {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount,
        source: 'fmr-both-origins',
        sections: failed ? undefined : response.sections,
        didError: failed,
      },
    });
  } else {
    deferred.reject(new Error('Cart refresh failed'));
  }
  if (failed) throw new Error(response.description || response.message || 'Add to cart failed');
  return cart;
}

/* ---------- 2. change a line's selling plan ---------- */
async function switchToClub(btn) {
  const key = btn.dataset.key;
  const qty = Number(btn.dataset.qty) || 1;
  const plan = Number(btn.dataset.plan);
  if (!key || !plan) return;

  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: key, quantity: qty }],
      promise: deferred.promise,
    })
  );

  let r;
  let data;
  try {
    r = await fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: qty,
        selling_plan: plan,
        sections: ids.join(','),
        sections_url: window.location.pathname,
      }),
    });
    data = await r.json();
  } catch (err) {
    deferred.reject(err);
    throw err;
  }

  if (!r.ok || data.status || data.errors) {
    // Shopify refused the change: report the real cart so listeners stay honest.
    const cart = await getCart().catch(() => null);
    if (cart) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'fmr-club-switch', didError: true },
      });
    } else {
      deferred.reject(new Error('Cart refresh failed'));
    }
    throw new Error(data.description || data.message || data.errors || 'Could not switch');
  }

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
    detail: {
      items: data.items,
      itemCount: data.item_count,
      source: 'fmr-club-switch',
      sections: data.sections || undefined,
      didError: false,
    },
  });
}

/* ---------- product page roast (shared by 3 and the add) ---------- */
function currentPdpRoast(scope) {
  const section = (scope && scope.closest('.shopify-section')) || document;
  const inputs = section.querySelectorAll('input[data-option-name]:checked');
  for (const input of inputs) {
    if (String(input.getAttribute('data-option-name')).toLowerCase().indexOf('roast') !== -1) return input.value;
  }
  return null;
}

function bothMap(wrap) {
  if (wrap._fmrMap) return wrap._fmrMap;
  try {
    wrap._fmrMap = JSON.parse(wrap.querySelector('[data-fmr-both-map]').textContent);
  } catch (e) {
    wrap._fmrMap = {};
  }
  return wrap._fmrMap;
}

function paintBoth(wrap, roast) {
  const entry = bothMap(wrap)[roast];
  if (!entry) return;
  wrap.dataset.roast = roast;
  const r = wrap.querySelector('[data-fmr-both-roast]');
  const p = wrap.querySelector('[data-fmr-both-price]');
  const label = wrap.querySelector('[data-fmr-both-label]');
  const btn = wrap.querySelector('[data-fmr-add-both]');
  if (r) r.textContent = roast;
  if (p) p.textContent = entry.price;
  if (btn) btn.disabled = !entry.ok;
  if (label) label.textContent = entry.ok ? 'Add both' : 'Sold out in ' + roast;
}

/* ---------- click delegation ---------- */
document.addEventListener('click', async (e) => {
  const addBtn = e.target.closest && e.target.closest('[data-fmr-add-both]');
  if (addBtn) {
    e.preventDefault();
    if (addBtn.getAttribute('aria-busy') === 'true') return;
    let items = null;
    if (addBtn.dataset.items) {
      try { items = JSON.parse(addBtn.dataset.items); } catch (err) { items = null; }
    } else {
      const wrap = addBtn.closest('[data-fmr-both]');
      const roast = (wrap.dataset.context === 'pdp' && currentPdpRoast(wrap)) || wrap.dataset.roast || 'Medium';
      const entry = bothMap(wrap)[roast];
      if (entry && entry.ok) items = entry.ids.map((id) => ({ id, quantity: 1 }));
    }
    if (!items || !items.length) return;

    addBtn.setAttribute('aria-busy', 'true');
    addBtn.disabled = true;
    setStatus(addBtn, '');
    try {
      await addItems(items, addBtn);
      setStatus(addBtn, items.length > 1 ? 'Added one bag of each.' : 'Added.');
      addBtn.classList.add('is-added');
      setTimeout(() => addBtn.classList.remove('is-added'), REDUCE ? 0 : 1400);
      // Give cart-drawer.js the first chance to open on its own.
      setTimeout(openDrawerIfClosed, 120);
    } catch (err) {
      setStatus(addBtn, 'That did not go through. ' + ((err && err.message) || 'Please try again.'));
    } finally {
      addBtn.removeAttribute('aria-busy');
      if (document.body.contains(addBtn)) addBtn.disabled = false;
    }
    return;
  }

  const clubBtn = e.target.closest && e.target.closest('[data-fmr-club-switch]');
  if (clubBtn) {
    e.preventDefault();
    if (clubBtn.getAttribute('aria-busy') === 'true') return;
    clubBtn.setAttribute('aria-busy', 'true');
    clubBtn.disabled = true;
    try {
      await switchToClub(clubBtn);
    } catch (err) {
      const sub = clubBtn.querySelector('.fmr-club-switch__sub');
      if (sub && document.body.contains(clubBtn)) sub.textContent = 'That did not go through. Tap to try again.';
    } finally {
      if (document.body.contains(clubBtn)) {
        clubBtn.removeAttribute('aria-busy');
        clubBtn.disabled = false;
      }
    }
  }
});

/* ---------- 3. product page: both-origins follows the Roast picker ---------- */
document.addEventListener('change', (e) => {
  const t = e.target;
  if (!t || !t.matches || !t.matches('input[data-option-name]')) return;
  if (String(t.getAttribute('data-option-name')).toLowerCase().indexOf('roast') === -1) return;
  const section = t.closest('.shopify-section') || document;
  section.querySelectorAll('[data-fmr-both][data-context="pdp"]').forEach((wrap) => paintBoth(wrap, t.value));
});

/* ---------- 4. sold-out swap on variant change ---------- */
document.addEventListener('shopify:product:select', (e) => {
  const target = e.target;
  if (!(target instanceof Element) || target.closest('product-card') || target.closest('quick-add-dialog')) return;
  const section = target.closest('.shopify-section') || document;
  const local = section.querySelectorAll('[data-fmr-notify]');
  if (!local.length || !e.promise) return;
  e.promise
    .then((res) => {
      const detail = (res && res.detail) || {};
      const fresh = detail.html && detail.html.querySelector && detail.html.querySelector('[data-fmr-notify]');
      let soldOut;
      if (fresh) soldOut = fresh.getAttribute('data-sold-out') === 'true';
      else if (detail.resource) soldOut = detail.resource.available === false;
      else soldOut = true; // no variant for this combination: nothing to buy
      local.forEach((el) => el.setAttribute('data-sold-out', soldOut ? 'true' : 'false'));
    })
    .catch(() => { /* leave the current state */ });
});

/* After a notify submit Shopify returns to ?customer_posted=true#<form id>.
   Only show the thank-you on THIS form's return, not after some other
   customer form (the footer signup) posts. */
function markPostedHere() {
  document.querySelectorAll('[data-fmr-notify][data-posted="true"]').forEach((el) => {
    const form = el.closest('form');
    if (form && form.id && window.location.hash === '#' + form.id) {
      el.setAttribute('data-posted-here', 'true');
      el.scrollIntoView({ block: 'center', behavior: REDUCE ? 'auto' : 'smooth' });
    }
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', markPostedHere);
else markPostedHere();
